import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  TrendingUp,
  Flame,
  Sparkles,
  Crown,
  Mail,
  Eye,
  CheckCircle2,
  ChevronRight,
  Radio,
  Headphones,
  Users,
} from "lucide-react";

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-5">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#B580FF]/30 to-[#5E8BFF]/30 border border-white/10 flex items-center justify-center">
      <Icon size={20} className="text-white" />
    </div>
    <div>
      <h2 className="font-display text-2xl font-semibold tracking-tight">{title}</h2>
      <p className="text-white/50 text-sm mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const StatPill = ({ label, value, accent }) => (
  <div
    data-testid={`ar-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className="bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-4 backdrop-blur"
  >
    <div className="text-xs uppercase tracking-[0.18em] text-white/40">{label}</div>
    <div
      className={`font-display text-3xl font-semibold mt-1 ${
        accent ? "bg-gradient-to-r from-[#B580FF] via-[#FF2E00] to-[#5E8BFF] bg-clip-text text-transparent" : ""
      }`}
    >
      {Number.isFinite(value) ? value.toLocaleString() : value}
    </div>
  </div>
);

const ArtistCard = ({ artist, rank, metricLabel, metricValue, linkTo }) => {
  const initials = (artist.name || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <Link
      to={linkTo}
      data-testid={`ar-artist-card-${artist.user_id || artist.itunes_artist_id || rank}`}
      className="group flex items-center gap-4 p-4 bg-white/[0.03] hover:bg-white/[0.07] border border-white/5 hover:border-white/15 rounded-2xl transition-all"
    >
      <div className="text-2xl font-display font-semibold text-white/30 w-8 text-right tabular-nums">
        {rank}
      </div>
      {artist.picture ? (
        <img
          src={artist.picture}
          alt={artist.name}
          className="w-14 h-14 rounded-xl object-cover ring-1 ring-white/10"
        />
      ) : (
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#B580FF]/40 to-[#5E8BFF]/40 flex items-center justify-center font-display text-lg font-semibold">
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-semibold truncate">{artist.name}</div>
          {artist.verified && (
            <CheckCircle2 size={14} className="text-[#5E8BFF] shrink-0" />
          )}
        </div>
        {artist.top_track && (
          <div className="text-xs text-white/40 truncate">Top: {artist.top_track}</div>
        )}
        {artist.bio && !artist.top_track && (
          <div className="text-xs text-white/40 truncate">{artist.bio}</div>
        )}
        {!artist.bio && !artist.top_track && artist.genre && (
          <div className="text-xs text-white/40 truncate">{artist.genre}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-xl font-semibold tabular-nums">
          {Number(metricValue || 0).toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">
          {metricLabel}
        </div>
      </div>
      <ChevronRight
        size={18}
        className="text-white/20 group-hover:text-white/60 group-hover:translate-x-1 transition-all"
      />
    </Link>
  );
};

const SignupBlock = () => {
  const [form, setForm] = useState({ name: "", email: "", label: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/ar/signup", form);
      setDone(true);
      toast.success("You're on the list. Weekly chart drops every Friday.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign you up");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="ar-signup-success"
        className="rounded-3xl border border-[#5E8BFF]/30 bg-gradient-to-br from-[#5E8BFF]/10 to-[#B580FF]/10 p-8 text-center"
      >
        <CheckCircle2 size={40} className="mx-auto text-[#5E8BFF] mb-3" />
        <h3 className="font-display text-2xl font-semibold mb-2">You&apos;re in.</h3>
        <p className="text-white/60 text-sm">
          We&apos;ll drop the 1o1sound A&R brief in your inbox every Friday — trending unsigned artists,
          biggest weekly jumpers, and direct links to their profiles.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
      <div className="flex items-center gap-3 mb-4">
        <Mail className="text-[#B580FF]" size={20} />
        <h3 className="font-display text-2xl font-semibold">Get the weekly A&R brief</h3>
      </div>
      <p className="text-white/60 text-sm mb-5">
        Trending unsigned artists, biggest weekly jumpers, new uploads — delivered Friday mornings.
        Free for verified industry contacts.
      </p>
      <form
        data-testid="ar-signup-form"
        onSubmit={submit}
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
      >
        <input
          data-testid="ar-signup-name"
          required
          placeholder="Full name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B580FF]/60 transition"
        />
        <input
          data-testid="ar-signup-email"
          required
          type="email"
          placeholder="work@label.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B580FF]/60 transition"
        />
        <input
          data-testid="ar-signup-label"
          placeholder="Label / company (optional)"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          className="bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B580FF]/60 transition"
        />
        <input
          data-testid="ar-signup-role"
          placeholder="Role (A&R, Scout, Journalist…)"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B580FF]/60 transition"
        />
        <button
          data-testid="ar-signup-submit"
          type="submit"
          disabled={submitting}
          className="md:col-span-2 bg-white text-black font-semibold rounded-xl px-6 py-3 hover:scale-[1.01] transition-all disabled:opacity-50"
        >
          {submitting ? "Subscribing…" : "Send me the weekly brief"}
        </button>
      </form>
    </div>
  );
};

export default function ARDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/ar/dashboard", { params: { limit: 20 } })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load dashboard");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="px-4 sm:px-8 py-16 text-white/40">Loading the A&R dashboard…</div>
    );
  }
  if (!data) {
    return (
      <div className="px-4 sm:px-8 py-16 text-white/40">
        Dashboard temporarily unavailable.
      </div>
    );
  }

  const { summary, trending_uploaders, weekly_gainers, new_talent, top_artists } = data;

  return (
    <div
      data-testid="ar-dashboard"
      className="px-4 sm:px-8 py-10 lg:py-14 max-w-6xl mx-auto"
    >
      {/* Hero */}
      <header className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Radio size={14} className="text-[#B580FF]" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-white/40">
            1o1sound · Industry intel
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight bg-gradient-to-r from-white via-white to-white/60 bg-clip-text text-transparent leading-[1.05]">
          A&R Dashboard
        </h1>
        <p className="text-white/55 text-base sm:text-lg mt-4 max-w-2xl">
          Real-time discovery for record labels, scouts, and music journalists. Find the unsigned
          artists building real audiences before everyone else does.
        </p>
      </header>

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
        <StatPill label="Artists" value={summary.total_artists} accent />
        <StatPill label="Uploaded tracks" value={summary.total_uploads} />
        <StatPill label="Streams this week" value={summary.streams_this_week} accent />
        <StatPill label="All-time streams" value={summary.streams_all_time} />
      </div>

      {/* Weekly Gainers */}
      <section className="mb-14">
        <SectionHeader
          icon={Flame}
          title="Weekly gainers"
          subtitle="Most-streamed artists on 1o1sound in the past 7 days"
        />
        {weekly_gainers.length === 0 ? (
          <div className="text-white/40 text-sm">
            No streams logged yet this week. Check back as plays start flowing in.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {weekly_gainers.map((a, i) => (
              <ArtistCard
                key={a.itunes_artist_id}
                rank={i + 1}
                artist={a}
                metricLabel="plays · 7d"
                metricValue={a.weekly_plays}
                linkTo={`/artist/${a.itunes_artist_id}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* New Talent */}
      <section className="mb-14">
        <SectionHeader
          icon={Sparkles}
          title="New talent"
          subtitle="Artists who uploaded their first track to 1o1sound in the past 14 days"
        />
        {new_talent.length === 0 ? (
          <div className="text-white/40 text-sm">
            No new artists this fortnight — be the first to spot them.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {new_talent.map((a, i) => (
              <ArtistCard
                key={a.user_id}
                rank={i + 1}
                artist={a}
                metricLabel="plays"
                metricValue={a.total_plays}
                linkTo={`/profile/${a.user_id}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Trending Uploaders */}
      <section className="mb-14">
        <SectionHeader
          icon={TrendingUp}
          title="Top uploaders"
          subtitle="1o1sound-native artists ranked by lifetime plays"
        />
        {trending_uploaders.length === 0 ? (
          <div className="text-white/40 text-sm">
            No uploads yet. As artists join and upload, they&apos;ll appear here.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {trending_uploaders.map((a, i) => (
              <ArtistCard
                key={a.user_id}
                rank={i + 1}
                artist={a}
                metricLabel="lifetime plays"
                metricValue={a.total_plays}
                linkTo={`/profile/${a.user_id}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Top Artists (catalog) */}
      <section className="mb-14">
        <SectionHeader
          icon={Crown}
          title="Top all-time"
          subtitle="Most-streamed artists on 1o1sound across every release"
        />
        {top_artists.length === 0 ? (
          <div className="text-white/40 text-sm">
            No stream data yet. Charts populate as listeners discover music.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {top_artists.map((a, i) => (
              <ArtistCard
                key={a.itunes_artist_id}
                rank={i + 1}
                artist={a}
                metricLabel="all-time"
                metricValue={a.all_time_plays}
                linkTo={`/artist/${a.itunes_artist_id}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Newsletter signup */}
      <SignupBlock />

      <footer className="text-center text-xs text-white/30 mt-12 pb-8">
        Generated {summary.generated_at ? new Date(summary.generated_at).toLocaleString() : "now"} ·
        Data refreshes live on every load · Built for labels by{" "}
        <span className="text-white/50">1o1sound</span>
      </footer>
    </div>
  );
}
